import { useEffect, useRef } from "react";
import { AgentProgress } from "@/components/AgentProgress";
import { QuestionCard } from "@/components/copilot/QuestionCard";
import { Bot, ArrowUp, Loader2, Sparkles, Eye } from "lucide-react";

// Minimal rich renderer: **bold**, `- ` / `* ` bullets, headings, blank-line paragraphs.
function RichText({ text }) {
  const nodes = [];
  let list = [];
  const flush = (key) => {
    if (list.length) { nodes.push(<ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-1">{list}</ul>); list = []; }
  };
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>);
  String(text).split("\n").forEach((ln, i) => {
    const t = ln.trim();
    if (/^#{1,6}\s+/.test(t)) { flush(i); nodes.push(<p key={`h-${i}`} className="font-semibold text-foreground mt-2 mb-1">{inline(t.replace(/^#{1,6}\s+/, ""))}</p>); }
    else if (/^[-*]\s+/.test(t)) list.push(<li key={`li-${i}`}>{inline(t.replace(/^[-*]\s+/, ""))}</li>);
    else { flush(i); if (t) nodes.push(<p key={`p-${i}`} className="my-1">{inline(t)}</p>); }
  });
  flush("end");
  return <>{nodes}</>;
}

const SUGGESTIONS = {
  recruiter: ["Create a Senior DevOps Engineer role", "Show candidates stuck more than 5 days", "How is our hiring funnel this month?"],
  candidate: ["Find remote roles matching my CV", "What skills am I missing for a SOC analyst?", "Make my profile stronger for cloud roles"],
};
const WELCOME = {
  recruiter: "I'm your Hiring Copilot. Tell me an outcome and I'll draft roles, screen candidates, or open the right workspace. What are we working on?",
  candidate: "I'm your Career Copilot. Ask me to find roles, sharpen your profile, or prep for an interview. What would you like to do?",
};

function Message({ m, i, last, busy, onAnswer }) {
  const mine = m.role === "user";
  return (
    <div className={mine ? "flex justify-end" : "flex gap-3"} data-testid={`ai-message-${i}`}>
      {!mine && <Bot className="h-5 w-5 text-primary shrink-0 mt-1" />}
      <div className={mine ? "max-w-[85%] rounded-2xl rounded-br-sm bg-primary/10 border border-primary/20 px-4 py-3 text-sm whitespace-pre-wrap break-words" : `flex-1 min-w-0 text-sm leading-7 break-words ${m.error ? "text-rose-500" : "text-foreground/90"}`}>
        {mine ? m.text : <RichText text={m.text} />}
        {m.questions?.length > 0 && <QuestionCard questions={m.questions} active={last && !busy} onSubmit={onAnswer} />}
      </div>
    </div>
  );
}

function Composer({ sessionId, session, input, setInput, send, busy }) {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (!el) return; el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 160)}px`; }, [input]);
  return (
    <div className="relative rounded-xl border border-border bg-card focus-within:border-primary/50 transition-colors">
      <textarea ref={ref} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
        placeholder="Message your copilot…" aria-label="Message your copilot" data-testid="copilot-input" rows={1} maxLength={12000}
        className="block w-full bg-transparent resize-none text-sm leading-6 py-3 pl-4 pr-14 outline-none min-h-[48px] max-h-40" />
      <button onClick={() => send()} disabled={busy || !input.trim() || (!!sessionId && !session)} aria-label="Send message" data-testid="copilot-send"
        className="absolute right-2 bottom-2 h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function CopilotChat({ role, sessionId, session, error, busy, input, setInput, send, previewCta, onOpenPreview, footer }) {
  const ref = useRef(null);
  const messages = session?.messages || [];
  const hasFooter = !!footer;
  useEffect(() => { const el = ref.current; if (el) el.scrollTop = el.scrollHeight; }, [messages.length, busy, hasFooter]);
  const refinement = session?.refinement?.status === "ready";
  return (
    <section className="ai-chat-panel h-full w-full" data-testid="ai-chat-panel">
      <div ref={ref} className="ai-messages" data-testid="copilot-messages" aria-live="polite">
        <div className="max-w-3xl mx-auto space-y-5 w-full">
          {error && <p role="alert" className="text-sm text-rose-500" data-testid="ai-session-error">{error}</p>}
          {!sessionId && <div className="py-12 space-y-4"><Sparkles className="h-8 w-8 text-primary" /><h1 className="font-display text-2xl">What shall we work on?</h1><p className="text-sm text-muted-foreground">{WELCOME[role]}</p></div>}
          {sessionId && !session && !error && <p className="text-sm text-muted-foreground" data-testid="ai-session-loading">Opening your conversation…</p>}
          {messages.map((m, i) => <Message key={i} m={m} i={i} last={i === messages.length - 1} busy={busy} onAnswer={send} />)}
          {session?.progress && <AgentProgress progress={session.progress} busy={busy} />}
          {busy && !session?.progress && <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="copilot-typing"><Loader2 className="h-4 w-4 motion-safe:animate-spin text-primary" /> Working on your request…</p>}
          {footer}
          {previewCta && refinement && !busy && (
            <button onClick={onOpenPreview} data-testid="review-question-changes"
              className="inline-flex items-center gap-2 text-sm text-primary border border-primary/30 rounded-lg px-4 py-2 hover:bg-primary/5 transition-colors">
              <Eye className="h-4 w-4" />Review proposed changes
            </button>
          )}
        </div>
      </div>
      <div className="ai-composer-wrap">
        <div className="max-w-3xl mx-auto w-full space-y-2">
          {!sessionId && <div className="flex flex-wrap gap-2">{SUGGESTIONS[role].map((s, i) => <button key={s} onClick={() => send(s)} data-testid={`ai-suggestion-${i}`} className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">{s}</button>)}</div>}
          <Composer sessionId={sessionId} session={session} input={input} setInput={setInput} send={send} busy={busy} />
          <p className="text-[10px] text-muted-foreground" data-testid="ai-save-status">{sessionId ? "Conversation saved privately to your account" : "Your first message opens a dedicated conversation tab"} · Shift + Enter for a new line</p>
        </div>
      </div>
    </section>
  );
}
